import { memo, useCallback } from 'react'

import { TextField } from '@mui/material'
import { AudioStreamInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SnackPanelActions, ViewSpace } from '@shared/components/Snackbar'
import {
  selectSubmissionProgressSetters,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { SnackPanelActionButtonLink } from 'components/LinkedMUIComponents'
import { useAudioStreamCreateMutation } from 'hooks/mutations/content'
import { audioStreamsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams'
import { audioStreamRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams/Stream'
import { workspaceQueryKeys } from 'services/queryKeys'

type FormStructure = AudioStreamInsert

const NewDialogRaw = () => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const { organizationId, environmentId, workspaceId } = audioStreamsOverviewRoute.useParams()

  const { setCurrentStep, setError: setSubmissionError } = useSubmissionProgressStore(
    useShallow(selectSubmissionProgressSetters),
  )

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const audioStreamCreateMutation = useAudioStreamCreateMutation()
  const { mutate: addStream } = audioStreamCreateMutation

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      repositoryId: workspace?.contentRepositoryId,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Audio.creating', { name: data.name }) })

      addStream(
        { audioStreamInsert: data },
        {
          onSuccess: (data) => {
            setCurrentStep({ value: 100, description: t('domain:Workspace.Audio.created', { name: data.name }) })

            setTimeout(() => {
              enqueueSnackbar(t('domain:Workspace.Audio.created', { name: data.name }), {
                description: `${t('domain:Workspace.Audio.createdDescription')}`,
                variant: 'panel',
                persist: true,
                icon: 'success',
                Actions: ({ onClose }) => (
                  <SnackPanelActions>
                    <SnackPanelActionButtonLink
                      to={audioStreamRoute.to}
                      params={{ organizationId, environmentId, workspaceId, audiostreamId: data.id }}
                      onClick={() => onClose()}
                    >
                      <ViewSpace />
                    </SnackPanelActionButtonLink>
                  </SnackPanelActions>
                ),
              })

              handleClose()
            }, 700)
          },
          onError: (_, variables) => {
            setSubmissionError()
            setCurrentStep({
              value: 60,
              description: `${variables.audioStreamInsert.name} ${t('domain:ContentProcessing.Instance.couldNotBeCreated')}`,
            })
          },
        },
      )
    },
    [
      addStream,
      enqueueSnackbar,
      environmentId,
      handleClose,
      organizationId,
      setCurrentStep,
      setSubmissionError,
      t,
      workspaceId,
    ],
  )

  return (
    <DialogFormContent
      title={t('domain:Workspace.Audio.addAudioStream')}
      description={t('domain:Workspace.Audio.Stream.addAudioStreamDescription')}
      onSubmit={handleSubmit(handleFormSubmit)}
      Illustration={null}
      SubmitProgressZone={
        <SubmissionProgress
          isSuccess={audioStreamCreateMutation.isSuccess}
          isError={audioStreamCreateMutation.isError}
        />
      }
    >
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            autoFocus
            margin="dense"
            label={t('name')}
            type="text"
            fullWidth
            variant="standard"
            required
            autoComplete="off"
          />
        )}
      />
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
